// src/app/shared/services/breadcrumb.service.ts
import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TENANT_MGMT_ROUTES } from '@app/+state/user-data/user.model';
import { AccessibleTenant, ITenantDetail, TenantContext, UserDataFacade } from '@ra-state';
import { IBreadcrumbItem } from '@ra-web-tech-ui-toolkit/components';
import * as R from 'remeda';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, startWith, take, tap } from 'rxjs/operators';

type BreadcrumbConfig = {
  label: string;
  suppressTenant?: boolean;
  suppressPrefix?: boolean;
  suppressChildTenant?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly userDataFacade = inject(UserDataFacade);

  private readonly breadcrumbsSubject$ = new BehaviorSubject<IBreadcrumbItem[]>([]);
  readonly breadcrumbs$: Observable<IBreadcrumbItem[]> = this.breadcrumbsSubject$.asObservable();
  private readonly titleSubject$ = new BehaviorSubject<string>('');
  readonly title$: Observable<string> = this.titleSubject$.asObservable();
  private isSubOrgSelected: boolean = false;
  private title: string = '';
  private accessibleTenants: AccessibleTenant[] = [];
  $navigateSignal = signal<string | null>(null);

  private readonly DASHBOARD_PATH = '/dashboard';
  private readonly SUB_ORGS_PATH = '/suborgs';

  private readonly subscriptions = new Subscription();
  private readonly routeNavigationEnd$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(this.router),
  );

  private readonly publishBreadcrumbs$ = combineLatest([
    this.routeNavigationEnd$,
    this.userDataFacade.currentTenantContext$,
    this.userDataFacade.currentTenant$,
    this.userDataFacade.accessibleTenants$,
  ]).pipe(
    tap(([_event, currentTenantContext, tenant, accessibleTenants]) => {
      this.accessibleTenants = accessibleTenants ?? [];
      const { routeBreadcrumbs, suppressTenant, suppressPrefix, suppressChildTenant } = this.generateBreadcrumbs();
      let breadcrumbs = routeBreadcrumbs;
      if (!suppressPrefix) {
        breadcrumbs = this.prefixTenantBreadcrumb(
          breadcrumbs,
          suppressTenant,
          currentTenantContext,
          tenant,
          suppressChildTenant,
        );
      }
      this.breadcrumbsSubject$.next(breadcrumbs);
    }),
  );

  url: string;

  constructor() {
    this.subscriptions.add(this.publishBreadcrumbs$.subscribe());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onClicked(event: IBreadcrumbItem): void {
    if (!event.id) {
      return;
    }

    if (event.label === 'Organizations') {
      this.$navigateSignal.set(event.id);
      return;
    }

    // route-level crumbs (e.g. Build Information) carry a URL as id, not a tenant id - just navigate
    if (String(event.id).startsWith('/')) {
      this.$navigateSignal.set(String(event.id));
      return;
    }

    this.userDataFacade.tenantChange(String(event.id));
    this.userDataFacade.hasAnyServiceRole$.pipe(take(1)).subscribe((tenantAccess) => {
      const navigateTo = tenantAccess === 'Service' ? this.DASHBOARD_PATH : this.SUB_ORGS_PATH;
      const targetUrl = `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${event.id}${navigateTo}`;

      if (this.isSubOrgSelected) {
        this.title = event.label;
        this.titleSubject$.next(event.label);
      }

      this.$navigateSignal.set(targetUrl);
    });
  }

  private generateBreadcrumbs(): {
    routeBreadcrumbs: IBreadcrumbItem[];
    suppressTenant: boolean;
    suppressPrefix: boolean;
    suppressChildTenant: boolean;
  } {
    let suppressTenant = false;
    let suppressPrefix = false;
    let suppressChildTenant = false;
    const breadcrumbs: IBreadcrumbItem[] = [];

    let currentRoute = this.activatedRoute.root;
    while (currentRoute?.firstChild) {
      currentRoute = currentRoute.firstChild;

      const breadcrumbData = currentRoute.snapshot.data?.['breadcrumb'] as BreadcrumbConfig;

      if (breadcrumbData?.suppressTenant) {
        suppressTenant = true;
      }
      if (breadcrumbData?.suppressPrefix) {
        suppressPrefix = true;
      }
      if (breadcrumbData?.suppressChildTenant) {
        suppressChildTenant = true;
      }
    }

    const route = this.activatedRoute;
    const routeURL: string = route?.snapshot?.url.map((segment) => segment.path).join('/');
    let url = '';
    if (routeURL) {
      url += `/${routeURL}`;
    }

    const breadcrumbData = route?.snapshot?.data?.['breadcrumb'] as BreadcrumbConfig;
    if (breadcrumbData) {
      breadcrumbs.push({ label: breadcrumbData.label, id: url });
    }
    // array breadcrumbs is mutated recursively
    const updatedBreadcrumbs = this.buildBreadcrumb(route, '', breadcrumbs);

    return { routeBreadcrumbs: updatedBreadcrumbs, suppressTenant, suppressPrefix, suppressChildTenant };
  }

  private buildBreadcrumb(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: IBreadcrumbItem[] = [],
  ): IBreadcrumbItem[] {
    let updatedBreadcrumbs: IBreadcrumbItem[] = breadcrumbs;
    const primaryChildren = route?.children || [];
    if (primaryChildren.length) {
      for (const child of route.children) {
        const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');
        const childUrl = routeURL ? `${url}/${routeURL}` : url;

        const breadcrumbData = child.snapshot.data?.['breadcrumb'] as BreadcrumbConfig;
        if (breadcrumbData) {
          breadcrumbs.push({ label: breadcrumbData.label, id: childUrl });
        }
        updatedBreadcrumbs = this.buildBreadcrumb(child, childUrl, breadcrumbs);
      }
    }
    return updatedBreadcrumbs;
  }

  private prefixTenantBreadcrumb(
    items: IBreadcrumbItem[],
    suppressTenantForCurrentRoute: boolean = false, // NOSONAR -- Preserve the existing optional argument behavior.
    currentTenantContext: TenantContext,
    tenant: ITenantDetail | undefined,
    suppressChildTenantForCurrentRoute: boolean = false,
  ): IBreadcrumbItem[] {
    if (currentTenantContext === 'Personal' || currentTenantContext === 'NotSelected' || R.isEmptyish(tenant)) {
      this.titleSubject$.next('');
      return [];
    }
    const breadcrumbPrefix: IBreadcrumbItem[] = [
      {
        label: 'Organizations',
        id: TENANT_MGMT_ROUTES.ORGANIZATIONS,
        icon: 'ra-icon-ide-sm-building-office',
      },
    ];

    if (!suppressTenantForCurrentRoute && tenant) {
      return this.buildTenantBreadcrumbs(tenant, items, suppressChildTenantForCurrentRoute);
    }

    this.resetTenantState();
    return breadcrumbPrefix.concat(items);
  }

  private buildTenantBreadcrumbs(
    tenant: ITenantDetail,
    items: IBreadcrumbItem[],
    suppressChildTenantForCurrentRoute: boolean = false,
  ): IBreadcrumbItem[] {
    const children = this.checkForSubOrg(tenant);
    const breadcrumbPrefix: IBreadcrumbItem[] = [
      {
        label: 'Organizations',
        id: TENANT_MGMT_ROUTES.ORGANIZATIONS,
        icon: 'ra-icon-ide-sm-building-office',
      },
    ];

    if (tenant.parent) {
      breadcrumbPrefix.push({
        label: this.getOrgName(tenant),
        id: String(tenant.parent),
        children,
      });
      if (!suppressChildTenantForCurrentRoute) {
        breadcrumbPrefix.push({
          label: tenant.name,
          id: tenant.id,
          children: [],
        });
      }
    } else {
      breadcrumbPrefix.push({
        label: tenant.name,
        id: tenant.id,
        children,
      });
    }

    this.updateTitleState(children, tenant);
    return breadcrumbPrefix.concat(items);
  }

  private updateTitleState(children: any[], tenant: ITenantDetail): void {
    if (children.length) {
      this.isSubOrgSelected = true;
      const title = this.title || tenant.name;
      this.titleSubject$.next(title);
    } else {
      this.resetTenantState();
    }
  }

  private resetTenantState(): void {
    this.title = '';
    this.titleSubject$.next('');
  }

  private checkForSubOrg(currentTenant: ITenantDetail | undefined): any {
    let children: any = [];
    if (currentTenant && this.accessibleTenants.length) {
      children = this.accessibleTenants
        .filter(
          (tenant) =>
            (tenant.parentId && tenant.parentId === currentTenant.id) ||
            (currentTenant.parent && tenant.parentId === currentTenant.parent),
        )
        .map((tenant) => ({
          label: tenant.shortName,
          id: tenant.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return children;
  }

  private getOrgName(currentTenant): string {
    if (!currentTenant?.parent) {
      return currentTenant.name;
    }
    const tenant = this.accessibleTenants.find((tenant) => tenant.id === currentTenant?.parent);
    return tenant ? tenant.name : '';
  }
}
